import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { getUsers, createUser, updateUser, deleteUser, getUserByEmail } from "../services/api";
import { getUserLevel, getUserId } from "../services/auth";
import UserModal from "../components/UserModal";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

// Função para aplicar a máscara de telefone (celular brasileiro)
// Reutilizada do UserModal.js
const applyPhoneMask = (value) => {
    // Garante que o valor é tratado como string
    let cleanedValue = String(value).replace(/\D/g, '');

    if (cleanedValue.length > 11) {
        cleanedValue = cleanedValue.substring(0, 11);
    }

    let maskedValue = '';

    if (cleanedValue.length > 0) {
        maskedValue = `(${cleanedValue.substring(0, 2)}`;
    }
    if (cleanedValue.length >= 3) {
        maskedValue += `) ${cleanedValue.substring(2, 3)}`;
    }
    if (cleanedValue.length >= 4) {
        maskedValue += ` ${cleanedValue.substring(3, 7)}`;
    }
    if (cleanedValue.length >= 8) {
        maskedValue += `-${cleanedValue.substring(7, 11)}`;
    }

    return maskedValue;
};


const UsersPage = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]); // Este estado ainda será usado para administradores
    const [userProfile, setUserProfile] = useState(null); // Estado para o perfil do usuário comum
    const [currentUser, setCurrentUser] = useState(null); // Usado para o modal de edição/criação
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const userLevel = getUserLevel();
    const loggedInUserId = getUserId();
    const loggedInUserEmail = localStorage.getItem('emailUser');

    useEffect(() => {
        if (!localStorage.getItem("token")) {
            navigate("/login");
            return;
        }

        if (userLevel === 'admin') {
            fetchUsers(); // Admins veem todos os usuários na tabela
        } else {
            // Usuários comuns veem apenas o seu perfil
            if (loggedInUserEmail) {
                fetchUserProfile(loggedInUserEmail);
            } else {
                setError("Não foi possível carregar o perfil do usuário logado. Email não encontrado.");
                setLoading(false);
            }
        }
    }, [navigate, userLevel, loggedInUserEmail]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError("");
            const response = await getUsers();
            setUsers(response.data || []);
        } catch (err) {
            setError("Erro ao carregar usuários");
            console.error(err);
            if (err.response?.status === 401) navigate("/login");
        } finally {
            setLoading(false);
        }
    };

    const fetchUserProfile = async (email) => {
        try {
            setLoading(true);
            setError("");
            const response = await getUserByEmail({ email: email });
            // CORREÇÃO AQUI: Acessar o primeiro elemento do array retornado pela API
            if (Array.isArray(response.data) && response.data.length > 0) {
                setUserProfile(response.data[0]);
            } else {
                setUserProfile(null); // Se a resposta for vazia ou não for um array
                setError("Perfil do usuário não encontrado.");
            }
        } catch (err) {
            setError("Erro ao carregar perfil do usuário");
            console.error(err);
            if (err.response?.status === 401) navigate("/login");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = () => {
        if (userLevel !== 'admin') {
            setError("Você não tem permissão para cadastrar novos usuários.");
            return;
        }
        setCurrentUser(null); // Para indicar que é um novo usuário
        setShowModal(true);
    };

    const handleEditUser = (user) => {
        // Admins podem editar qualquer usuário da tabela.
        // Usuários comuns podem editar APENAS o seu próprio perfil (que é o 'userProfile' para eles).
        if (userLevel === 'admin' || (userLevel !== 'admin' && user.id === parseInt(loggedInUserId))) {
            setCurrentUser(user);
            setShowModal(true);
        } else {
            setError("Você não tem permissão para editar este perfil.");
        }
    };

    const handleSaveUser = async (userData) => {
        try {
            if (currentUser) {
                // Se estiver editando um usuário existente
                if (userLevel !== 'admin' && currentUser.id !== parseInt(loggedInUserId)) {
                    setError("Você não tem permissão para alterar este perfil.");
                    return;
                }
                await updateUser(currentUser.id, { ...userData, id: currentUser.id });
            } else {
                // Se estiver criando um novo usuário (somente para admin)
                if (userLevel !== 'admin') {
                    setError("Você não tem permissão para cadastrar novos usuários.");
                    return;
                }
                await createUser(userData);
            }

            // Re-fetch os dados apropriados após salvar
            if (userLevel === 'admin') {
                fetchUsers();
            } else {
                fetchUserProfile(loggedInUserEmail); // Re-fetch o perfil do usuário comum
            }
            setShowModal(false);
        } catch (error) {
            console.error("Erro ao salvar usuário:", error);
            setError(error.response?.data?.message || "Erro ao salvar usuário");
        }
    };

    const handleDeleteUser = async (id) => {
        if (userLevel !== 'admin') {
            setError("Você não tem permissão para excluir usuários.");
            return;
        }
        if (window.confirm("Tem certeza que deseja excluir este usuário?")) {
            try {
                await deleteUser(id);
                fetchUsers(); // Re-busca todos os usuários para admin
            } catch (error) {
                console.error("Erro ao excluir usuário:", error);
                setError(error.response?.data?.message || "Erro ao excluir usuário");
            }
        }
    };

    // Esta função não está sendo usada na tabela, mas é boa para ter.
    const formatDate = (dateString) => {
        try {
            if (!dateString) return "N/A";
            const date = typeof dateString === "string" ? parseISO(dateString) : new Date(dateString);
            return format(date, "dd/MM/yyyy HH:mm", { locale: ptBR });
        } catch (error) {
            console.error("Erro ao formatar data:", error);
            return "Data inválida";
        }
    };

    return (
        <div>
            <Header />
            <div
                className="container"
                style={{ maxWidth: "1200px", margin: "50px auto", padding: "20px" }}
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "20px",
                    }}
                >
                    <h1>{userLevel === 'admin' ? "Gerenciamento de Usuários" : "Meu Perfil"}</h1>
                    {userLevel === 'admin' && (
                        <button
                            onClick={handleCreateUser}
                            style={{
                                padding: "10px 20px",
                                backgroundColor: "#2ecc71",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontWeight: "600",
                                fontSize: "16px",
                            }}
                        >
                            Cadastrar Novo Usuário
                        </button>
                    )}
                </div>

                {error && (
                    <div
                        style={{
                            backgroundColor: "#f8d7da",
                            color: "#721c24",
                            padding: "10px",
                            borderRadius: "4px",
                            marginBottom: "20px",
                            border: "1px solid #f5c6cb",
                        }}
                    >
                        {error}
                    </div>
                )}

                {loading ? (
                    <p>Carregando...</p>
                ) : (
                    <>
                        {userLevel === 'admin' ? (
                            // VISÃO DO ADMINISTRADOR: Tabela de todos os usuários
                            <div style={{ overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead>
                                        <tr style={{ backgroundColor: "#f2f2f2" }}>
                                            <th style={tableHeaderStyle}>Nome</th>
                                            <th style={tableHeaderStyle}>Email</th>
                                            <th style={tableHeaderStyle}>Telefone</th> {/* Aplicado estilo aqui */}
                                            <th style={tableHeaderStyle}>Nível</th>
                                            <th style={tableHeaderStyle}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.map((user) => (
                                            <tr key={user.id} style={{ borderBottom: "1px solid #ddd" }}>
                                                <td style={tableCellStyle}>{user.nome || "N/A"}</td>
                                                <td style={tableCellStyle}>{user.email || "N/A"}</td>
                                                {/* NOVO: Aplica a máscara na exibição do telefone na tabela */}
                                                <td style={tableCellStyle}>{user.telefone ? applyPhoneMask(user.telefone) : "N/A"}</td>
                                                <td style={tableCellStyle}>{user.nivel || "N/A"}</td>
                                                <td style={tableCellStyle}>
                                                    <button
                                                        onClick={() => handleEditUser(user)}
                                                        style={{
                                                            padding: "6px 12px", backgroundColor: "#3498db", color: "white",
                                                            border: "none", borderRadius: "4px", cursor: "pointer", marginRight: "5px",
                                                        }}
                                                    >
                                                        Editar
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteUser(user.id)}
                                                        style={{
                                                            padding: "6px 12px", backgroundColor: "#e74c3c", color: "white",
                                                            border: "none", borderRadius: "4px", cursor: "pointer",
                                                        }}
                                                    >
                                                        Excluir
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            // VISÃO DO USUÁRIO COMUM: Apenas seu perfil
                            userProfile ? (
                                <div style={{
                                    backgroundColor: "#fff",
                                    padding: "30px",
                                    borderRadius: "8px",
                                    boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                                    maxWidth: "500px",
                                    margin: "0 auto",
                                    textAlign: "center"
                                }}>
                                    <h2 style={{ color: "#34495e", marginBottom: "25px" }}>Meu Perfil</h2>
                                    <div style={{ marginBottom: "15px" }}>
                                        <p style={{ margin: "5px 0", color: "#555", fontSize: "1.1rem" }}>
                                            <strong>Nome:</strong> {userProfile.nome || "N/A"}
                                        </p>
                                        <p style={{ margin: "5px 0", color: "#555", fontSize: "1.1rem" }}>
                                            <strong>Email:</strong> {userProfile.email || "N/A"}
                                        </p>
                                        <p style={{ margin: "5px 0", color: "#555", fontSize: "1.1rem" }}>
                                            {/* NOVO: Aplica a máscara na exibição do telefone no perfil comum */}
                                            <strong>Telefone:</strong> {userProfile.telefone ? applyPhoneMask(userProfile.telefone) : "N/A"}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => handleEditUser(userProfile)} // Passa o userProfile para edição
                                        style={{
                                            padding: "10px 25px",
                                            backgroundColor: "#3498db",
                                            color: "white",
                                            border: "none",
                                            borderRadius: "5px",
                                            cursor: "pointer",
                                            fontWeight: "bold",
                                            marginTop: "20px"
                                        }}
                                    >
                                        Editar Meu Perfil
                                    </button>
                                </div>
                            ) : (
                                <p>Nenhum perfil encontrado ou erro ao carregar.</p>
                            )
                        )}
                    </>
                )}

                {showModal && (
                    <UserModal
                        user={currentUser} // currentUser será o perfil do usuário comum para eles mesmos
                        onClose={() => setShowModal(false)}
                        onSave={handleSaveUser}
                        // Prop para o modal saber quais campos desabilitar para um usuário comum
                        isEditingOwnProfile={userLevel !== 'admin' && !!currentUser}
                    />
                )}
            </div>
        </div>
    );
};

// Estilos de tabela (reutilizados para consistência)
const tableHeaderStyle = { padding: "12px", textAlign: "left", borderBottom: "1px solid #ddd", fontSize: "0.9rem" };
const tableCellStyle = { padding: "12px", fontSize: "0.9rem" };


export default UsersPage;